import React from "react";
import { bool, elementType, node, oneOf, shape } from "prop-types";
import { Col, Row } from "../Grid";
import Link from "../Link";
import Section from "../Section";
import Heading from "../Heading";
import Block from "../Block";
import ContentContainer from "../r1/atoms/ContentContainer";
import { classes } from "../utils/classes";
import pureProps, { deepMergeProps } from "../utils/pureProps";
import { colorPropTypes, sxClassName, sxProps } from "../props/sx";
import { spacingClassName, spacingProps } from "../props/spacing";
import { utilityClassName, utilityProps } from "../props/utility";

const Title = ({
	className = "",
	color,
	utility = { ...utilityProps },
	sx = { ...sxProps },
	spacing = { ...spacingProps },
	children,
	...props
}) => (
	<Heading
		Tag="h2"
		className={classes(
			"landing-simple-section__title",
			...utilityClassName(utility),
			...sxClassName(
				deepMergeProps({ fontColor: color, textAlign: "center" }, pureProps(sx)),
				true,
			),
			...spacingClassName(deepMergeProps({ mb: 0, mt: 0 }, pureProps(spacing))),
			className,
		)}
		{...props}
	>
		{children}
	</Heading>
);

Title.propTypes = {
	...Heading.propTypes,
};

const TitleV2 = ({
	className = "",
	color,
	children,
	utility = { ...utilityProps },
	sx = { ...sxProps },
	spacing = { ...spacingProps },
	...props
}) => (
	<Heading
		Tag="h2"
		className={classes(
			"landing-simple-section__title-v2",
			...utilityClassName(utility),
			...sxClassName(
				deepMergeProps({ fontColor: color, textAlign: "center" }, pureProps(sx)),
				true,
			),
			...spacingClassName(deepMergeProps({ mb: 0, mt: 0 }, pureProps(spacing))),
			className,
		)}
		{...props}
	>
		{children}
	</Heading>
);

TitleV2.propTypes = {
	...Heading.propTypes,
};

const DescriptionV2 = ({
	className = "",
	color,
	children,
	utility = { ...utilityProps },
	sx = { ...sxProps },
	spacing = { ...spacingProps },
}) => (
	<Block
		className={classes(
			"landing-simple-section__description-v2",
			...utilityClassName(utility),
			...sxClassName(
				deepMergeProps({ fontColor: color, fontSize: "l", textAlign: "center" }, pureProps(sx)),
			),
			...spacingClassName(deepMergeProps({ mt: 4 }, pureProps(spacing))),
			className,
		)}
	>
		{children}
	</Block>
);

DescriptionV2.propTypes = {
	color: colorPropTypes,
	...Block.propTypes,
};

const Body = ({ children = null }) => <>{children}</>;

Body.propTypes = {
	children: node,
};

const Action = ({ ActionComponent = Link, children, ...props }) => (
	<Row utility={{ justify: "center" }} spacing={{ mt: 8 }}>
		<Col sx={{ textAlign: "center" }}>
			<ActionComponent {...props}>{children}</ActionComponent>
		</Col>
	</Row>
);

Action.propTypes = {
	ActionComponent: elementType,
	children: node.isRequired,
};

const LandingSimpleSection = ({
	className = "",
	isBorderNone = false,
	bgColor,
	utility = { ...utilityProps },
	spacing = { ...spacingProps },
	children,
	containerProps,
	...props
}) => {
	return (
		<Section
			className={classes(
				"landing-simple-section",
				!isBorderNone && "landing-simple-section--border-bottom",
				...utilityClassName(deepMergeProps({ bgColor }, pureProps(utility))),
				...spacingClassName(spacing),
				className,
			)}
			{...props}
		>
			<ContentContainer {...containerProps}>{children}</ContentContainer>
		</Section>
	);
};

LandingSimpleSection.propTypes = {
	bgColor: colorPropTypes,
	isBorderNone: bool,
	containerProps: shape({
		sm: oneOf(["", "auto", "shrink", 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
		md: oneOf(["", "auto", "shrink", 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
		lg: oneOf(["", "auto", "shrink", 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
		center: bool,
	}),
	...Section.propTypes,
};

const SectionLabel = ({
	utility = { ...utilityProps },
	sx = { ...sxProps },
	spacing = { ...spacingProps },
	className = "",
	children,
}) => {
	return (
		<Block
			className={classes(
				"landing-simple-section__label",
				...utilityClassName(utility),
				...sxClassName(sx),
				...spacingClassName(spacing),
				className,
			)}
		>
			{children}
		</Block>
	);
};

SectionLabel.propTypes = {
	...Block.propTypes,
};

LandingSimpleSection.Label = SectionLabel;
LandingSimpleSection.Title = Title;
LandingSimpleSection.TitleV2 = TitleV2;
LandingSimpleSection.DescriptionV2 = DescriptionV2;
LandingSimpleSection.Body = Body;
LandingSimpleSection.Action = Action;

export default LandingSimpleSection;
